import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Shield, Award, Clock, Star, ArrowRight, BookOpen, Heart, Sparkles } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { getSafeSsoParam } from '../utils/ssoUrlSafety';

const API_URL = getApiUrl();

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_f760df05-0c76-4756-9fc9-778e88d148c4/artifacts/drje6014_Logo.png';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const GarbhsanskarFullPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  // UTM/Meta attribution — pass through to the webhook for LMS audit
  const utm = {
    utm_source: searchParams.get('utm_source') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    fbc: searchParams.get('fbc') || undefined,
    fbp: searchParams.get('fbp') || undefined,
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/public/pricing?country=India`)
      .then((res) => setPricing(res.data))
      .catch(() => setPricing(null))
      .finally(() => setLoadingPricing(false));
  }, []);

  const fullPrice = pricing?.garbhsanskar_full_price || 1499;
  const strikeoff = pricing?.garbhsanskar_full_strikeoff || 2999;
  const currency = pricing?.currency || 'INR';
  const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
  const savings = strikeoff && fullPrice ? Math.round(((strikeoff - fullPrice) / strikeoff) * 100) : 0;

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Name, email, and phone are all required');
      return;
    }
    if (!form.email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setProcessing(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Payment gateway failed to load. Check your connection.');
        setProcessing(false);
        return;
      }

      const event_id = `evt_gsf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const orderRes = await axios.post(`${API_URL}/api/public/garbhsanskar-full/create-order`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        country: 'India',
        currency,
        event_id,
        ...utm,
      });

      const { order_id, amount_paise, currency: orderCurrency, razorpay_key, lead_id } = orderRes.data;

      const options = {
        key: razorpay_key,
        amount: amount_paise,
        currency: orderCurrency,
        name: 'Soul Sanskar',
        description: 'Garbhsanskar Full Course — All 9 Episodes',
        order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#6366f1' },
        handler: async (resp) => {
          try {
            const verifyRes = await axios.post(`${API_URL}/api/public/garbhsanskar-full/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              lead_id,
              event_id,
            });
            toast.success('Payment successful! Welcome to Garbhsanskar.');
            const thankYouUrl = new URL(window.location.origin + '/garbhsanskar-full/thank-you');
            thankYouUrl.searchParams.set('name', form.name);
            thankYouUrl.searchParams.set('email', form.email);
            if (verifyRes.data?.lms_sso_url) {
              thankYouUrl.searchParams.set('sso', verifyRes.data.lms_sso_url);
            }
            navigate(thankYouUrl.pathname + thankYouUrl.search);
          } catch (verifyErr) {
            toast.error(verifyErr.response?.data?.detail || 'Payment verification failed');
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  if (loadingPricing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-rose-50 flex items-center justify-center">
        <div className="text-slate-600" data-testid="gsf-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-rose-50" data-testid="garbhsanskar-full-landing">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center">
          <img src={LOGO_URL} alt="Soul Sanskar" className="h-10" />
          <span className="ml-3 text-lg font-semibold text-slate-900">Soul Sanskar</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Left — pitch */}
        <section>
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Modern Garbhsanskar Masterclass
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            Full course access.<br />
            <span className="text-indigo-600">All 9 episodes.</span>
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Ancient wisdom, modern science. Connect with your baby through scientifically-backed
            prenatal practices taught by certified prenatal educators.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { icon: BookOpen, text: 'All 9 masterclass episodes, lifetime access' },
              { icon: Heart, text: 'Weekly guided meditation & garbhasanskar audio' },
              { icon: Award, text: 'Certified by Certified Prenatal Yoga Educators' },
              { icon: Shield, text: '7-day money-back guarantee, no questions asked' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`gsf-benefit-${i}`}>
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-sm text-slate-700 pt-1.5">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 pt-6 border-t border-slate-200">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-rose-400 border-2 border-white" />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-xs font-medium text-slate-700 ml-1">4.9/5</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Trusted by 2,800+ mothers across India</p>
            </div>
          </div>
        </section>

        {/* Right — checkout card */}
        <section>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 lg:p-8 sticky top-24">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-3xl lg:text-4xl font-bold text-slate-900">{symbol}{fullPrice.toLocaleString('en-IN')}</span>
              {strikeoff && (
                <span className="text-lg text-slate-400 line-through">{symbol}{strikeoff.toLocaleString('en-IN')}</span>
              )}
              {savings > 0 && (
                <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                  SAVE {savings}%
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-6">One-time payment · GST included · No recurring charges</p>

            <form onSubmit={handleBuy} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Your Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Priya Sharma"
                  data-testid="gsf-form-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="you@example.com"
                  data-testid="gsf-form-email"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Phone (WhatsApp) *</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="+91 98765 43210"
                  data-testid="gsf-form-phone"
                />
                <p className="text-xs text-slate-500 mt-1">We'll send your course access link here</p>
              </div>

              <button
                type="submit"
                disabled={processing}
                className="w-full mt-2 py-3.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="gsf-submit-btn"
              >
                {processing ? (
                  'Processing…'
                ) : (
                  <>
                    Unlock Full Course · {symbol}{fullPrice.toLocaleString('en-IN')}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Secure Razorpay
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Instant access
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default GarbhsanskarFullPage;


// ─── Thank-you page (SSO auto-redirect) ────────────────────────────────────

export const GarbhsanskarFullThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') || 'there';
  // V28 — SSO read from sessionStorage first (head-script stashed it before
  // any tracker fired), URL fallback for backward compat.
  const ssoUrl = getSafeSsoParam(searchParams, 'sso');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!ssoUrl) return;
    const t = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          window.location.href = ssoUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [ssoUrl]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-rose-50 flex items-center justify-center p-4" data-testid="gsf-thank-you">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome, {name}!</h1>
        <p className="text-slate-600 mb-6">
          Your Garbhsanskar Full Course is unlocked. All 9 episodes are ready for you.
        </p>
        {ssoUrl ? (
          <>
            <p className="text-sm text-slate-500 mb-4">
              Redirecting you to your dashboard in <span className="font-semibold text-indigo-600">{countdown}</span> seconds…
            </p>
            <a
              href={ssoUrl}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
              data-testid="gsf-thank-you-go-btn"
            >
              Go to My Dashboard <ArrowRight className="w-4 h-4" />
            </a>
          </>
        ) : (
          <a
            href="https://academy.soulsanskar.com/learner/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
            data-testid="gsf-thank-you-fallback-btn"
          >
            Go to My Dashboard <ArrowRight className="w-4 h-4" />
          </a>
        )}
        <p className="text-xs text-slate-400 mt-6">
          A confirmation email + WhatsApp message are on their way.
        </p>
      </div>
    </div>
  );
};
